import React, { useState } from 'react';
import { CheckCircle, Clock, Database, Download, Eye, Lock, Mail, PenTool, RefreshCw, Search, Send, Trash2, X, Plus, FileText, Globe } from 'lucide-react';

// Use relative URLs on production (same-origin) to avoid CORS issues
const BACKEND_URL = window.location.hostname === 'sierrascala.com' || window.location.hostname === 'www.sierrascala.com'
  ? ''
  : process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [authenticated, setAuthenticated] = useState(() => {
    return typeof window !== 'undefined' && sessionStorage.getItem('admin_auth') === 'true';
  });
  const [loginEmail, setLoginEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [loginError, setLoginError] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('contacts');
  const [campaigns, setCampaigns] = useState([]);
  const [emailPreview, setEmailPreview] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [composeSent, setComposeSent] = useState(false);
  // LinkedIn state
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [linkedinDrafts, setLinkedinDrafts] = useState([]);
  const [linkedinGenerating, setLinkedinGenerating] = useState(false);
  const [linkedinPublishing, setLinkedinPublishing] = useState(null);
  const [linkedinEditId, setLinkedinEditId] = useState(null);
  const [linkedinEditText, setLinkedinEditText] = useState('');
  const [linkedinCustomText, setLinkedinCustomText] = useState('');
  const [scheduleModalId, setScheduleModalId] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  // Proposals state
  const [proposalTemplates, setProposalTemplates] = useState([]);
  const [showNewProposal, setShowNewProposal] = useState(false);
  const [newProposal, setNewProposal] = useState({
    company_name: '', slug: '', access_code: '', tagline: '', prepared_for: '',
    primary_color: '#3b82f6', accent_color: '#1e293b'
  });
  const [newSections, setNewSections] = useState([]);
  const [proposalSaving, setProposalSaving] = useState(false);
  // Follow-up state
  const [followupEnabled, setFollowupEnabled] = useState(false);
  const [followupQueue, setFollowupQueue] = useState([]);
  const [followupStats, setFollowupStats] = useState({ pending: 0, sent: 0, failed: 0 });

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password })
      });
      if (res.ok) {
        setAdminPassword(password);
        sessionStorage.setItem('admin_auth', 'true');
        sessionStorage.setItem('admin_pw', password);
        setAuthenticated(true);
        setLoginError(false);
      } else {
        setLoginError(true);
      }
    } catch {
      setLoginError(true);
    }
  };

  const fetchContacts = async () => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contacts?password=${encodeURIComponent(pw)}`);
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts || []);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const deleteContact = async (id) => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    try {
      await fetch(`${BACKEND_URL}/api/contacts/${id}?password=${encodeURIComponent(pw)}`, { method: 'DELETE' });
      setContacts(contacts.filter(c => c.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const exportCSV = () => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    window.open(`${BACKEND_URL}/api/contacts/export?password=${encodeURIComponent(pw)}`, '_blank');
  };

  const fetchCampaigns = async () => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    try {
      const res = await fetch(`${BACKEND_URL}/api/email-tracking?password=${encodeURIComponent(pw)}`);
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (err) { console.error(err); }
  };

  const loadEmailPreview = async () => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    try {
      const res = await fetch(`${BACKEND_URL}/api/email-preview/megatel-followup?password=${encodeURIComponent(pw)}`);
      if (res.ok) {
        const data = await res.json();
        setEmailPreview(data);
      }
    } catch (err) { console.error(err); }
  };

  const sendFollowUp = async (resend = false) => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    setSendingEmail(true);
    setSendResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/email-send/megatel-followup?password=${encodeURIComponent(pw)}${resend ? '&resend=true' : ''}`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        setSendResult({ success: true, message: data.message });
        fetchCampaigns();
      } else {
        setSendResult({ success: false, message: data.detail || 'Failed to send' });
      }
    } catch (err) {
      setSendResult({ success: false, message: 'Network error' });
    }
    setSendingEmail(false);
  };

  const resetTrackingStats = async (trackingId) => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    try {
      await fetch(`${BACKEND_URL}/api/email-tracking/${trackingId}/reset?password=${encodeURIComponent(pw)}`, { method: 'POST' });
      fetchCampaigns();
    } catch (err) { console.error(err); }
  };

  const openReplyComposer = () => {
    setComposeTo('carlos.ravines@megatelhomes.com');
    setComposeSubject('Re: Follow-up Marketing Specialist Position and Custom Proposal');
    setComposeBody(`Hi Carlos,

Thank you for getting back to me — and yes, Ana was kind enough to connect us!

Absolutely, the commute to Uptown Dallas is very feasible for me. I'm in Arlington, so it's a straightforward 30-45 minute drive depending on traffic. I'm completely comfortable with an onsite position and honestly prefer the collaboration that comes with being in-office, especially for a marketing role where creative synergy matters.

I'd love the opportunity to discuss the position further and walk you through the marketing strategy I put together for Megatel. Would you have time for a quick call or in-person meeting this week?

I'm flexible with scheduling — whatever works best for your calendar.

Looking forward to it!

Thank you,

Sierra Scala
Digital Marketing Strategist

C 817.470.0933
me@sierrascala.com
sierrascala.com`);
    setComposerOpen(true);
    setComposeSent(false);
  };

  const sendComposedEmail = async () => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    setComposeSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/email-send/compose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_email: composeTo, subject: composeSubject, body_text: composeBody, password: pw })
      });
      if (res.ok) {
        setComposeSent(true);
        fetchCampaigns();
      }
    } catch (err) { console.error(err); }
    setComposeSending(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    sessionStorage.removeItem('admin_pw');
    setAuthenticated(false);
    setPassword('');
    setAdminPassword('');
    setContacts([]);
  };

  // LinkedIn functions
  const fetchLinkedinStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/linkedin/status`);
      if (res.ok) setLinkedinStatus(await res.json());
    } catch (err) { console.error(err); }
  };

  const fetchLinkedinDrafts = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/linkedin/drafts`);
      if (res.ok) setLinkedinDrafts(await res.json());
    } catch (err) { console.error(err); }
  };

  const generateLinkedinDraft = async () => {
    setLinkedinGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/linkedin/generate-draft`, { method: 'POST' });
      if (res.ok) fetchLinkedinDrafts();
    } catch (err) { console.error(err); }
    setLinkedinGenerating(false);
  };

  const generateLinkedinBatch = async () => {
    setLinkedinGenerating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/linkedin/generate-batch?count=3`, { method: 'POST' });
      if (res.ok) fetchLinkedinDrafts();
    } catch (err) { console.error(err); }
    setLinkedinGenerating(false);
  };

  const publishLinkedinDraft = async (draftId) => {
    setLinkedinPublishing(draftId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/linkedin/drafts/${draftId}/publish?with_image=true`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        alert(`Published to LinkedIn!\n${data.chars_sent || '?'} characters / ${data.words_sent || '?'} words sent.${data.has_image ? '\nAI image attached.' : ''}`);
        fetchLinkedinDrafts();
      } else {
        alert(data.error || 'Failed to publish');
      }
    } catch (err) { console.error(err); }
    setLinkedinPublishing(null);
  };

  const deleteLinkedinDraft = async (draftId) => {
    try {
      await fetch(`${BACKEND_URL}/api/linkedin/drafts/${draftId}`, { method: 'DELETE' });
      setLinkedinDrafts(linkedinDrafts.filter(d => d.id !== draftId));
    } catch (err) { console.error(err); }
  };

  const saveLinkedinEdit = async (draftId) => {
    try {
      await fetch(`${BACKEND_URL}/api/linkedin/drafts/${draftId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: linkedinEditText })
      });
      setLinkedinEditId(null);
      fetchLinkedinDrafts();
    } catch (err) { console.error(err); }
  };

  const postLinkedinCustom = async () => {
    if (!linkedinCustomText.trim()) return;
    setLinkedinPublishing('custom');
    try {
      const res = await fetch(`${BACKEND_URL}/api/linkedin/post-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: linkedinCustomText })
      });
      const data = await res.json();
      if (data.success) {
        setLinkedinCustomText('');
        fetchLinkedinDrafts();
      } else {
        alert(data.error || data.message || 'Failed to post');
      }
    } catch (err) { console.error(err); }
    setLinkedinPublishing(null);
  };

  const scheduleLinkedinDraft = async (draftId) => {
    if (!scheduleDate || !scheduleTime) return;
    const scheduledFor = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
    try {
      const res = await fetch(`${BACKEND_URL}/api/linkedin/drafts/${draftId}/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduled_for: scheduledFor })
      });
      const data = await res.json();
      if (data.success) {
        setScheduleModalId(null);
        setScheduleDate('');
        setScheduleTime('');
        fetchLinkedinDrafts();
      } else {
        alert(data.error || data.detail || 'Failed to schedule');
      }
    } catch (err) { console.error(err); }
  };

  const unscheduleLinkedinDraft = async (draftId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/linkedin/drafts/${draftId}/unschedule`, { method: 'POST' });
      if (res.ok) fetchLinkedinDrafts();
    } catch (err) { console.error(err); }
  };

  // Proposal functions
  const fetchProposals = async () => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    try {
      const res = await fetch(`${BACKEND_URL}/api/proposals/templates?password=${encodeURIComponent(pw)}`);
      if (res.ok) {
        const data = await res.json();
        setProposalTemplates(data.templates || []);
      }
    } catch (err) { console.error(err); }
  };

  const createProposal = async () => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    setProposalSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/proposals/templates?password=${encodeURIComponent(pw)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newProposal, sections: newSections })
      });
      if (res.ok) {
        setShowNewProposal(false);
        setNewProposal({ company_name: '', slug: '', access_code: '', tagline: '', prepared_for: '', primary_color: '#3b82f6', accent_color: '#1e293b' });
        setNewSections([]);
        fetchProposals();
      } else {
        const err = await res.json();
        alert(err.detail || 'Failed to create proposal');
      }
    } catch (err) { console.error(err); }
    setProposalSaving(false);
  };

  const deleteProposal = async (id) => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    try {
      await fetch(`${BACKEND_URL}/api/proposals/templates/${id}?password=${encodeURIComponent(pw)}`, { method: 'DELETE' });
      fetchProposals();
    } catch (err) { console.error(err); }
  };

  const addSection = (type) => {
    setNewSections([...newSections, { type, title: '', subtitle: '', content: '', bullets: [], stats: [] }]);
  };

  const updateSection = (idx, field, value) => {
    const updated = [...newSections];
    updated[idx] = { ...updated[idx], [field]: value };
    setNewSections(updated);
  };

  const removeSection = (idx) => {
    setNewSections(newSections.filter((_, i) => i !== idx));
  };

  // Follow-up functions
  const fetchFollowupSettings = async () => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    try {
      const res = await fetch(`${BACKEND_URL}/api/followups/settings?password=${encodeURIComponent(pw)}`);
      if (res.ok) {
        const data = await res.json();
        setFollowupEnabled(data.enabled || false);
      }
    } catch (err) { console.error(err); }
  };

  const toggleFollowups = async () => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    try {
      const res = await fetch(`${BACKEND_URL}/api/followups/settings?password=${encodeURIComponent(pw)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !followupEnabled })
      });
      if (res.ok) {
        setFollowupEnabled(!followupEnabled);
      }
    } catch (err) { console.error(err); }
  };

  const fetchFollowupQueue = async () => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    try {
      const res = await fetch(`${BACKEND_URL}/api/followups/queue?password=${encodeURIComponent(pw)}`);
      if (res.ok) {
        const data = await res.json();
        setFollowupQueue(data.queue || []);
        setFollowupStats(data.stats || { pending: 0, sent: 0, failed: 0 });
      }
    } catch (err) { console.error(err); }
  };

  const cancelFollowup = async (itemId) => {
    const pw = adminPassword || sessionStorage.getItem('admin_pw') || '';
    try {
      await fetch(`${BACKEND_URL}/api/followups/queue/${itemId}?password=${encodeURIComponent(pw)}`, { method: 'DELETE' });
      fetchFollowupQueue();
    } catch (err) { console.error(err); }
  };

  React.useEffect(() => {
    if (authenticated) {
      fetchContacts();
      fetchCampaigns();
      loadEmailPreview();
      fetchLinkedinStatus();
      fetchLinkedinDrafts();
      fetchProposals();
      fetchFollowupSettings();
      fetchFollowupQueue();
    }
  }, [authenticated]);

  const filtered = contacts.filter(c =>
    (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (c.company || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <div className="max-w-sm w-full">
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Lock className="text-slate-400" size={20} />
            </div>
            <h1 className="text-xl font-bold text-white">Admin</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <input
              type="email"
              value={loginEmail}
              onChange={(e) => { setLoginEmail(e.target.value); setLoginError(false); }}
              placeholder="Email"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              data-testid="admin-email-input"
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setLoginError(false); }}
                placeholder="Password"
                className={`w-full px-4 py-3 bg-white/10 border ${loginError ? 'border-red-500' : 'border-white/20'} rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 pr-12`}
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <X size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {loginError && <p className="text-red-400 text-xs text-center" data-testid="admin-login-error">Invalid credentials</p>}
            <button type="submit" className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors" data-testid="admin-login-submit">Sign In</button>
          </form>
          <div className="text-center mt-4">
            <a href="/" className="text-slate-500 hover:text-slate-300 text-sm">Back to site</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4" data-testid="admin-header">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center">
              <Database className="text-white" size={16} />
            </div>
            <h1 className="text-lg font-bold text-slate-800">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-slate-400 hover:text-slate-600 text-sm">Site</a>
            <button onClick={handleLogout} className="text-red-400 hover:text-red-600 text-sm" data-testid="admin-logout-btn">Logout</button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-6">
        <div className="max-w-6xl mx-auto flex gap-1">
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'contacts' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-contacts"
          >
            Contacts <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-xs ml-1">{contacts.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('campaigns')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'campaigns' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-campaigns"
          >
            Email Campaigns <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-xs ml-1">{campaigns.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('linkedin')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'linkedin' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-linkedin"
          >
            LinkedIn <span className={`px-1.5 py-0.5 rounded text-xs ml-1 ${linkedinStatus?.connected ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-600'}`}>{linkedinStatus?.connected ? 'Connected' : 'Setup'}</span>
          </button>
          <button
            onClick={() => setActiveTab('proposals')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'proposals' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-proposals"
          >
            Proposals <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded text-xs ml-1">{proposalTemplates.length}</span>
          </button>
          <button
            onClick={() => setActiveTab('followups')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'followups' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="tab-followups"
          >
            Follow-ups <span className={`px-1.5 py-0.5 rounded text-xs ml-1 ${followupEnabled ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-600'}`}>{followupEnabled ? 'On' : 'Off'}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="admin-dashboard">

        {/* ========== CAMPAIGNS TAB ========== */}
        {activeTab === 'campaigns' && (
          <div>
            {/* Compose Email */}
            {composerOpen && (
              <div className="bg-white rounded-xl border border-slate-200 mb-8 overflow-hidden" data-testid="email-composer">
                <div className="px-6 py-4 bg-blue-50 border-b border-blue-200 flex items-center justify-between">
                  <h2 className="font-bold text-slate-800">Compose Email</h2>
                  <button onClick={() => setComposerOpen(false)} className="text-slate-400 hover:text-slate-600">
                    <X size={18} />
                  </button>
                </div>
                {composeSent ? (
                  <div className="p-8 text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="text-green-600" size={24} />
                    </div>
                    <p className="font-semibold text-green-700">Email sent to {composeTo}</p>
                    <button onClick={() => { setComposerOpen(false); setComposeSent(false); }} className="mt-4 text-sm text-slate-500 hover:text-slate-700">Close</button>
                  </div>
                ) : (
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">From</label>
                        <div className="px-3 py-2 bg-slate-50 rounded-lg text-sm text-slate-600 border border-slate-200">me@sierrascala.com</div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">To</label>
                        <input
                          type="email"
                          value={composeTo}
                          onChange={(e) => setComposeTo(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid="compose-to"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500 mb-1 block">Subject</label>
                      <input
                        type="text"
                        value={composeSubject}
                        onChange={(e) => setComposeSubject(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="compose-subject"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500 mb-1 block">Message</label>
                      <textarea
                        value={composeBody}
                        onChange={(e) => setComposeBody(e.target.value)}
                        rows={18}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-sans leading-relaxed"
                        data-testid="compose-body"
                      />
                    </div>
                    <div className="flex items-center justify-between pt-2">
                      <button onClick={() => setComposerOpen(false)} className="text-sm text-slate-500 hover:text-slate-700">Cancel</button>
                      <button
                        onClick={sendComposedEmail}
                        disabled={composeSending || !composeTo || !composeSubject || !composeBody}
                        className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                        data-testid="compose-send-btn"
                      >
                        <Send size={16} /> {composeSending ? 'Sending...' : 'Send Email'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Quick Actions */}
            {!composerOpen && (
              <div className="flex gap-3 mb-6">
                <button
                  onClick={openReplyComposer}
                  className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  data-testid="reply-carlos-btn"
                >
                  <Send size={16} /> Reply to Carlos
                </button>
                <button
                  onClick={() => { setComposeTo(''); setComposeSubject(''); setComposeBody(''); setComposerOpen(true); setComposeSent(false); }}
                  className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors"
                  data-testid="new-email-btn"
                >
                  <Mail size={16} /> New Email
                </button>
              </div>
            )}

            {/* Megatel Follow-Up Email */}
            <div className="bg-white rounded-xl border border-slate-200 mb-8 overflow-hidden" data-testid="followup-email-section">
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-slate-800">Megatel Follow-Up Email</h2>
                  <p className="text-xs text-slate-500">To: carlos.ravines@megatelhomes.com</p>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => { fetchCampaigns(); loadEmailPreview(); }} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
                    <RefreshCw size={14} /> Refresh
                  </button>
                </div>
              </div>

              {emailPreview && (
                <div className="p-6">
                  {/* Email Meta */}
                  <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                    <div className="bg-slate-50 rounded-lg p-3">
                      <span className="text-slate-500">From:</span> <span className="font-medium text-slate-800 ml-1">{emailPreview.from}</span>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <span className="text-slate-500">Reply-To:</span> <span className="font-medium text-slate-800 ml-1">{emailPreview.reply_to}</span>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <span className="text-slate-500">To:</span> <span className="font-medium text-slate-800 ml-1">{emailPreview.to}</span>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3">
                      <span className="text-slate-500">Subject:</span> <span className="font-medium text-slate-800 ml-1">{emailPreview.subject}</span>
                    </div>
                  </div>

                  {/* Email Preview */}
                  <div className="border border-slate-200 rounded-xl p-6 mb-6 bg-white">
                    <p className="text-xs text-slate-400 uppercase font-semibold mb-3">Email Preview</p>
                    <div dangerouslySetInnerHTML={{ __html: (emailPreview.html_body || '').replace(/<img[^>]*track\/open[^>]*>/gi, '') }} />
                  </div>

                  {/* Tracking Info */}
                  <div className="bg-blue-50 rounded-lg p-4 mb-6 border border-blue-200">
                    <p className="text-sm font-medium text-blue-800 mb-2">Tracking Enabled</p>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>Open tracking — hidden pixel detects when Carlos opens the email</li>
                      <li>Click tracking — Portfolio link and Proposal link routed through tracker</li>
                      <li>Results appear in the "Sent Campaigns" section below after sending</li>
                    </ul>
                  </div>

                  {/* Send Button */}
                  {sendResult ? (
                    <div className={`rounded-lg p-4 text-center ${sendResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="send-result">
                      <p className={`font-medium ${sendResult.success ? 'text-green-700' : 'text-red-700'}`}>{sendResult.message}</p>
                    </div>
                  ) : (
                    <button
                      onClick={sendFollowUp}
                      disabled={sendingEmail}
                      className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                      data-testid="send-followup-btn"
                    >
                      <Send size={18} /> {sendingEmail ? 'Sending...' : 'Send Follow-Up to Carlos Ravines'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Sent Campaigns / Tracking Results */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="tracking-section">
              <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                <h2 className="font-bold text-slate-800">Sent Campaigns</h2>
              </div>
              {campaigns.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Mail className="mx-auto mb-2" size={32} />
                  <p className="font-medium">No campaigns sent yet</p>
                  <p className="text-xs mt-1">Send the follow-up email above to start tracking.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {campaigns.map((c) => (
                    <div key={c.tracking_id} className="p-6" data-testid={`campaign-${c.tracking_id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-slate-800">{c.subject}</h3>
                          <p className="text-xs text-slate-500">To: {c.to_email} — Sent: {new Date(c.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                        </div>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${c.sent ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {c.sent ? 'Sent' : 'Failed'}
                        </span>
                        <button onClick={() => resetTrackingStats(c.tracking_id)} className="text-xs text-slate-400 hover:text-red-500 ml-2" data-testid={`reset-stats-${c.tracking_id}`}>
                          Reset Stats
                        </button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-slate-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-slate-800" data-testid={`opens-${c.tracking_id}`}>{c.open_count || 0}</p>
                          <p className="text-xs text-slate-500">Opens</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-slate-800" data-testid={`clicks-${c.tracking_id}`}>{c.click_count || 0}</p>
                          <p className="text-xs text-slate-500">Link Clicks</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-slate-800">{(c.opens || []).length > 0 ? 'Yes' : 'No'}</p>
                          <p className="text-xs text-slate-500">Opened?</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-4 text-center">
                          <p className="text-2xl font-bold text-slate-800">{(c.clicks || []).length > 0 ? 'Yes' : 'No'}</p>
                          <p className="text-xs text-slate-500">Clicked Link?</p>
                        </div>
                      </div>
                      {(c.opens || []).length > 0 && (
                        <div className="mt-4 bg-green-50 rounded-lg p-3 border border-green-200">
                          <p className="text-xs font-semibold text-green-700 mb-1">Open Events:</p>
                          {c.opens.map((o, i) => (
                            <p key={i} className="text-xs text-green-600">{new Date(o.timestamp).toLocaleString()}</p>
                          ))}
                        </div>
                      )}
                      {(c.clicks || []).length > 0 && (
                        <div className="mt-3 bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <p className="text-xs font-semibold text-blue-700 mb-1">Click Events:</p>
                          {c.clicks.map((cl, i) => (
                            <p key={i} className="text-xs text-blue-600">{cl.url} — {new Date(cl.timestamp).toLocaleString()}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== FOLLOW-UPS (within Campaigns conceptually, own tab) ========== */}
        {activeTab === 'followups' && (
          <div data-testid="followups-tab">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Email Follow-up Automation</h2>
                <p className="text-sm text-slate-500 mt-1">Automatic drip emails when someone requests proposal access (Day 0, Day 3, Day 7)</p>
              </div>
              <button
                onClick={toggleFollowups}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${followupEnabled ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`}
                data-testid="followup-toggle"
              >
                {followupEnabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>

            {/* Sequence Preview */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <h3 className="font-semibold text-slate-800 mb-4">Follow-up Sequence</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">0d</div>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">Immediate Thank You</p>
                    <p className="text-xs text-slate-500">Thanks for requesting access + portfolio link</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3d</div>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">Check-in</p>
                    <p className="text-xs text-slate-500">Did you review the proposal? + Calendly link</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">7d</div>
                  <div>
                    <p className="font-medium text-slate-800 text-sm">Final Follow-up</p>
                    <p className="text-xs text-slate-500">Let's connect — schedule a call CTA</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                <p className="text-2xl font-bold text-blue-600" data-testid="followup-pending">{followupStats.pending}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                <p className="text-2xl font-bold text-green-600" data-testid="followup-sent">{followupStats.sent}</p>
                <p className="text-xs text-slate-500">Sent</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5 text-center">
                <p className="text-2xl font-bold text-red-600" data-testid="followup-failed">{followupStats.failed}</p>
                <p className="text-xs text-slate-500">Failed</p>
              </div>
            </div>

            {/* Queue */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">Follow-up Queue</h3>
                <button onClick={fetchFollowupQueue} className="text-sm text-slate-500 hover:text-slate-700"><RefreshCw size={14} /></button>
              </div>
              {followupQueue.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">No follow-ups in queue. They'll appear here when someone requests proposal access.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {followupQueue.slice(0, 20).map(item => (
                    <div key={item.id} className="p-4 flex items-center justify-between" data-testid={`followup-item-${item.id}`}>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          item.status === 'sent' ? 'bg-green-100 text-green-700' :
                          item.status === 'failed' ? 'bg-red-100 text-red-700' :
                          item.status === 'cancelled' ? 'bg-slate-100 text-slate-500' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {item.status}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{item.contact?.name} ({item.contact?.email})</p>
                          <p className="text-xs text-slate-400">Step {item.step} — Day {item.delay_days} — Send: {new Date(item.send_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                        </div>
                      </div>
                      {item.status === 'pending' && (
                        <button onClick={() => cancelFollowup(item.id)} className="text-xs text-red-400 hover:text-red-600 px-2 py-1 hover:bg-red-50 rounded" data-testid={`cancel-followup-${item.id}`}>
                          Cancel
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ========== LINKEDIN TAB ========== */}
        {activeTab === 'linkedin' && (
          <div data-testid="linkedin-tab">
            {/* Connection Status */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="linkedin-connection-status">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-slate-800 text-lg">LinkedIn Connection</h2>
                {linkedinStatus?.connected && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${linkedinStatus?.health === 'healthy' ? 'bg-green-100 text-green-700' : linkedinStatus?.health === 'expired' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                    {linkedinStatus?.health === 'healthy' ? 'Healthy' : linkedinStatus?.health === 'expired' ? 'Token Expired' : 'Unknown'}
                  </span>
                )}
              </div>
              {linkedinStatus?.connected ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Connected as <strong>{linkedinStatus.user_name}</strong> ({linkedinStatus.user_email})</p>
                    {linkedinStatus.connected_at && <p className="text-xs text-slate-400 mt-1">Connected: {new Date(linkedinStatus.connected_at).toLocaleDateString()}</p>}
                    {linkedinStatus.can_post === false && (
                      <p className="text-xs text-amber-600 mt-1 bg-amber-50 px-2 py-1 rounded inline-block">Posting not yet available — "Share on LinkedIn" product still pending approval in your LinkedIn Developer App</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { fetch(`${BACKEND_URL}/api/linkedin/refresh`, { method: 'POST' }).then(() => fetchLinkedinStatus()); }} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-lg transition-colors" data-testid="linkedin-refresh-btn">Refresh Token</button>
                    <button onClick={() => { fetch(`${BACKEND_URL}/api/linkedin/disconnect`, { method: 'POST' }).then(() => { setLinkedinStatus({ connected: false }); }); }} className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs rounded-lg transition-colors" data-testid="linkedin-disconnect-btn">Disconnect</button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-500 mb-4">Connect your LinkedIn account to enable auto-posting.</p>
                  <div className="flex flex-col items-center gap-3">
                    <a href={`${BACKEND_URL || ''}/api/linkedin/authorize`} className="inline-block px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors" data-testid="linkedin-connect-btn">Connect LinkedIn</a>
                    <a href={`${BACKEND_URL || ''}/api/linkedin/authorize?full=true`} className="text-xs text-slate-400 hover:text-blue-600 transition-colors" data-testid="linkedin-connect-full-btn">Connect with posting permissions (requires "Share on LinkedIn" product approved)</a>
                  </div>
                </div>
              )}
            </div>

            {/* Generate Posts */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-slate-800 text-lg">AI Post Generator</h2>
                <div className="flex gap-2">
                  <button
                    onClick={generateLinkedinDraft}
                    disabled={linkedinGenerating}
                    className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                    data-testid="linkedin-generate-one"
                  >
                    {linkedinGenerating ? 'Generating...' : 'Generate 1 Post'}
                  </button>
                  <button
                    onClick={generateLinkedinBatch}
                    disabled={linkedinGenerating}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                    data-testid="linkedin-generate-batch"
                  >
                    {linkedinGenerating ? 'Generating...' : 'Generate 3 Posts'}
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-500">AI generates LinkedIn posts about social media strategy, video production, data-driven marketing, and more — tailored to your background.</p>
            </div>

            {/* Custom Post */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <h2 className="font-bold text-slate-800 mb-3">Quick Post</h2>
              <textarea
                value={linkedinCustomText}
                onChange={(e) => setLinkedinCustomText(e.target.value)}
                placeholder="Write a custom LinkedIn post..."
                className="w-full p-3 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                data-testid="linkedin-custom-textarea"
              />
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-slate-400">{linkedinCustomText.length} characters</span>
                <button
                  onClick={postLinkedinCustom}
                  disabled={!linkedinCustomText.trim() || linkedinPublishing === 'custom' || !linkedinStatus?.connected}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                  data-testid="linkedin-post-custom"
                >
                  {linkedinPublishing === 'custom' ? 'Posting...' : 'Post to LinkedIn'}
                </button>
              </div>
            </div>

            {/* Drafts & History */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-bold text-slate-800">Posts & Drafts <span className="text-xs text-slate-400 font-normal ml-2">{linkedinDrafts.length} total</span></h2>
                <div className="flex gap-2 text-xs">
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">Drafts: {linkedinDrafts.filter(d => d.status === 'draft').length}</span>
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Scheduled: {linkedinDrafts.filter(d => d.status === 'scheduled').length}</span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded">Published: {linkedinDrafts.filter(d => d.status === 'published').length}</span>
                </div>
              </div>
              {linkedinDrafts.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-sm">No posts yet. Generate some drafts to get started!</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {linkedinDrafts.map(draft => (
                    <div key={draft.id} className="p-4 hover:bg-slate-50" data-testid={`linkedin-draft-${draft.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              draft.status === 'published' ? 'bg-green-100 text-green-700' :
                              draft.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                              draft.status === 'failed' ? 'bg-red-100 text-red-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {draft.status === 'published' ? 'Published' : draft.status === 'scheduled' ? 'Scheduled' : draft.status === 'failed' ? 'Failed' : 'Draft'}
                            </span>
                            {draft.status === 'scheduled' && draft.scheduled_for && (
                              <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">
                                <Clock size={10} />
                                {new Date(draft.scheduled_for).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              </span>
                            )}
                            {draft.status === 'failed' && draft.error && (
                              <span className="px-2 py-0.5 rounded text-xs bg-red-50 text-red-500" title={draft.error}>Error: {draft.error.substring(0, 50)}</span>
                            )}
                            {draft.has_image && <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">With Image</span>}
                            <span className="text-xs text-slate-400">{draft.topic}</span>
                            <span className="text-xs text-slate-400">{new Date(draft.created_at).toLocaleDateString()}</span>
                          </div>
                          {linkedinEditId === draft.id ? (
                            <div>
                              <textarea
                                value={linkedinEditText}
                                onChange={(e) => setLinkedinEditText(e.target.value)}
                                className="w-full p-3 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500"
                                rows={10}
                                data-testid={`linkedin-edit-textarea-${draft.id}`}
                              />
                              <div className="flex gap-2 mt-2">
                                <button onClick={() => saveLinkedinEdit(draft.id)} className="px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg">Save</button>
                                <button onClick={() => setLinkedinEditId(null)} className="px-3 py-1.5 bg-slate-100 text-slate-600 text-xs rounded-lg">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-slate-700 whitespace-pre-line">{draft.text}</p>
                          )}
                        </div>
                        {draft.status === 'draft' && linkedinEditId !== draft.id && (
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => { setLinkedinEditId(draft.id); setLinkedinEditText(draft.text); }}
                              className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"
                              title="Edit"
                              data-testid={`linkedin-edit-${draft.id}`}
                            >
                              <PenTool size={14} />
                            </button>
                            <button
                              onClick={() => { setScheduleModalId(draft.id); setScheduleDate(''); setScheduleTime(''); }}
                              className="p-1.5 hover:bg-blue-50 rounded text-blue-400 hover:text-blue-600"
                              title="Schedule for later"
                              data-testid={`linkedin-schedule-${draft.id}`}
                            >
                              <Clock size={14} />
                            </button>
                            <button
                              onClick={() => publishLinkedinDraft(draft.id)}
                              disabled={linkedinPublishing === draft.id || !linkedinStatus?.connected}
                              className="p-1.5 hover:bg-green-50 rounded text-green-500 hover:text-green-700 disabled:opacity-50"
                              title="Publish now (with AI image)"
                              data-testid={`linkedin-publish-${draft.id}`}
                            >
                              {linkedinPublishing === draft.id ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                            </button>
                            <button
                              onClick={() => deleteLinkedinDraft(draft.id)}
                              className="p-1.5 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                              title="Delete"
                              data-testid={`linkedin-delete-${draft.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                        {draft.status === 'scheduled' && (
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => unscheduleLinkedinDraft(draft.id)}
                              className="p-1.5 hover:bg-amber-50 rounded text-amber-500 hover:text-amber-700"
                              title="Cancel schedule (revert to draft)"
                              data-testid={`linkedin-unschedule-${draft.id}`}
                            >
                              <X size={14} />
                            </button>
                            <button
                              onClick={() => deleteLinkedinDraft(draft.id)}
                              className="p-1.5 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                              title="Delete"
                              data-testid={`linkedin-delete-scheduled-${draft.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                        {draft.status === 'failed' && (
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => { setScheduleModalId(draft.id); setScheduleDate(''); setScheduleTime(''); }}
                              className="p-1.5 hover:bg-blue-50 rounded text-blue-400 hover:text-blue-600"
                              title="Reschedule"
                              data-testid={`linkedin-reschedule-${draft.id}`}
                            >
                              <Clock size={14} />
                            </button>
                            <button
                              onClick={() => publishLinkedinDraft(draft.id)}
                              disabled={linkedinPublishing === draft.id || !linkedinStatus?.connected}
                              className="p-1.5 hover:bg-green-50 rounded text-green-500 hover:text-green-700 disabled:opacity-50"
                              title="Retry publish now"
                              data-testid={`linkedin-retry-${draft.id}`}
                            >
                              <RefreshCw size={14} />
                            </button>
                            <button
                              onClick={() => deleteLinkedinDraft(draft.id)}
                              className="p-1.5 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                              title="Delete"
                              data-testid={`linkedin-delete-failed-${draft.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                        {draft.status === 'published' && (
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => deleteLinkedinDraft(draft.id)}
                              className="p-1.5 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                              title="Delete from dashboard"
                              data-testid={`linkedin-delete-published-${draft.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Schedule Modal */}
        {scheduleModalId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="schedule-modal-overlay" onClick={() => setScheduleModalId(null)}>
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()} data-testid="schedule-modal">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="text-blue-600" size={20} />
                <h3 className="font-bold text-slate-800 text-lg">Schedule Post</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">Choose when this post should be published to LinkedIn (with AI-generated image).</p>
              <div className="space-y-3 mb-5">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Date</label>
                  <input
                    type="date"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="schedule-date-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Time</label>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="schedule-time-input"
                  />
                </div>
              </div>
              {scheduleDate && scheduleTime && (
                <p className="text-xs text-slate-500 mb-4 bg-slate-50 p-2 rounded-lg">
                  Will post on <strong>{new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</strong>
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => scheduleLinkedinDraft(scheduleModalId)}
                  disabled={!scheduleDate || !scheduleTime}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="schedule-confirm-btn"
                >
                  Schedule Post
                </button>
                <button
                  onClick={() => setScheduleModalId(null)}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-lg transition-colors"
                  data-testid="schedule-cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========== PROPOSALS TAB ========== */}
        {activeTab === 'proposals' && (
          <div data-testid="proposals-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-slate-800">Proposal Templates</h2>
              <button
                onClick={() => setShowNewProposal(!showNewProposal)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                data-testid="new-proposal-btn"
              >
                <Plus size={16} /> New Proposal
              </button>
            </div>

            {/* New Proposal Form */}
            {showNewProposal && (
              <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6" data-testid="new-proposal-form">
                <h3 className="font-bold text-slate-800 mb-4">Create New Proposal</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Company Name *</label>
                    <input value={newProposal.company_name} onChange={e => setNewProposal({...newProposal, company_name: e.target.value})} placeholder="Acme Corp" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="proposal-company-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">URL Slug * <span className="text-slate-400">(e.g. "acme" → /proposals/acme)</span></label>
                    <input value={newProposal.slug} onChange={e => setNewProposal({...newProposal, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})} placeholder="acme" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="proposal-slug-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Access Code *</label>
                    <input value={newProposal.access_code} onChange={e => setNewProposal({...newProposal, access_code: e.target.value})} placeholder="acme2026" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="proposal-code-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Prepared For</label>
                    <input value={newProposal.prepared_for} onChange={e => setNewProposal({...newProposal, prepared_for: e.target.value})} placeholder="Jane Smith, CMO" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="proposal-prepfor-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Tagline</label>
                    <input value={newProposal.tagline} onChange={e => setNewProposal({...newProposal, tagline: e.target.value})} placeholder="Digital Growth Strategy" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" data-testid="proposal-tagline-input" />
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-slate-600 block mb-1">Primary Color</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={newProposal.primary_color} onChange={e => setNewProposal({...newProposal, primary_color: e.target.value})} className="w-8 h-8 rounded cursor-pointer" />
                        <input value={newProposal.primary_color} onChange={e => setNewProposal({...newProposal, primary_color: e.target.value})} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-medium text-slate-600 block mb-1">Accent Color</label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={newProposal.accent_color} onChange={e => setNewProposal({...newProposal, accent_color: e.target.value})} className="w-8 h-8 rounded cursor-pointer" />
                        <input value={newProposal.accent_color} onChange={e => setNewProposal({...newProposal, accent_color: e.target.value})} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sections Builder */}
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-slate-700 text-sm">Sections ({newSections.length})</h4>
                    <div className="flex gap-1 flex-wrap">
                      {['hero', 'audit', 'strategy', 'stats', 'bullets', 'cta', 'custom'].map(type => (
                        <button key={type} onClick={() => addSection(type)} className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-lg capitalize transition-colors" data-testid={`add-section-${type}`}>
                          + {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {newSections.map((sec, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-4 mb-3 relative" data-testid={`section-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-blue-100 text-blue-700">{sec.type}</span>
                        <button onClick={() => removeSection(idx)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                      </div>
                      <div className="space-y-2">
                        <input value={sec.title || ''} onChange={e => updateSection(idx, 'title', e.target.value)} placeholder="Section title" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                        {(sec.type === 'hero') && (
                          <input value={sec.subtitle || ''} onChange={e => updateSection(idx, 'subtitle', e.target.value)} placeholder="Subtitle" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                        )}
                        {['strategy', 'cta', 'custom'].includes(sec.type) && (
                          <textarea value={sec.content || ''} onChange={e => updateSection(idx, 'content', e.target.value)} placeholder="Content text..." rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none" />
                        )}
                        {['audit', 'strategy', 'bullets', 'custom'].includes(sec.type) && (
                          <div>
                            <label className="text-xs text-slate-500">Bullet points (one per line)</label>
                            <textarea
                              value={(sec.bullets || []).join('\n')}
                              onChange={e => updateSection(idx, 'bullets', e.target.value.split('\n').filter(b => b.trim()))}
                              placeholder="One bullet per line..."
                              rows={3}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                            />
                          </div>
                        )}
                        {sec.type === 'stats' && (
                          <div>
                            <label className="text-xs text-slate-500">Stats (format: metric | label, one per line)</label>
                            <textarea
                              value={(sec.stats || []).map(s => `${s.metric} | ${s.label}`).join('\n')}
                              onChange={e => updateSection(idx, 'stats', e.target.value.split('\n').filter(l => l.includes('|')).map(l => { const [m, ...rest] = l.split('|'); return { metric: m.trim(), label: rest.join('|').trim() }; }))}
                              placeholder="+200% | Organic Traffic&#10;50+ | Leads/Month"
                              rows={3}
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    onClick={createProposal}
                    disabled={!newProposal.company_name || !newProposal.slug || !newProposal.access_code || proposalSaving}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                    data-testid="create-proposal-btn"
                  >
                    {proposalSaving ? 'Creating...' : 'Create Proposal'}
                  </button>
                  <button onClick={() => setShowNewProposal(false)} className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-lg transition-colors">Cancel</button>
                </div>
              </div>
            )}

            {/* Existing Templates List */}
            {proposalTemplates.length === 0 && !showNewProposal ? (
              <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
                <FileText className="text-slate-300 mx-auto mb-3" size={40} />
                <p className="text-slate-500 font-medium">No proposal templates yet</p>
                <p className="text-slate-400 text-sm mt-1">Create your first reusable proposal template above.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {proposalTemplates.map(t => (
                  <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between" data-testid={`proposal-template-${t.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${t.primary_color}15` }}>
                        <FileText size={18} style={{ color: t.primary_color }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{t.company_name}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-slate-400">/{t.slug}</span>
                          <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Code: {t.access_code}</span>
                          <span className="text-xs text-slate-400">{t.sections?.length || 0} sections</span>
                          {t.tagline && <span className="text-xs text-slate-400">{t.tagline}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={`/proposals/${t.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-blue-600 transition-colors" title="Preview">
                        <Globe size={16} />
                      </a>
                      <button onClick={() => deleteProposal(t.id)} className="p-2 hover:bg-red-50 rounded-lg text-red-400 hover:text-red-600 transition-colors" title="Delete" data-testid={`delete-proposal-${t.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ========== CONTACTS TAB ========== */}
        {activeTab === 'contacts' && (
          <div>
        {/* Controls */}
        <div className="flex items-center justify-between mb-6">
          <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full" data-testid="contact-count">{contacts.length} contacts</span>
          <div className="flex items-center gap-3">
            <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors" data-testid="export-csv-btn">
              <Download size={16} /> Export CSV
            </button>
            <button onClick={fetchContacts} className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-colors" data-testid="refresh-btn">
              <RefreshCw size={16} /> Refresh
            </button>
          </div>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <p className="text-sm text-slate-500">Total Contacts</p>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-total">{contacts.length}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <p className="text-sm text-slate-500">With Company</p>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-with-company">{contacts.filter(c => c.company).length}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <p className="text-sm text-slate-500">This Week</p>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-this-week">
              {contacts.filter(c => {
                const d = new Date(c.created_at);
                const now = new Date();
                return (now - d) < 7 * 24 * 60 * 60 * 1000;
              }).length}
            </p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-slate-200">
            <p className="text-sm text-slate-500">Megatel Requests</p>
            <p className="text-2xl font-bold text-slate-800" data-testid="stat-megatel">{contacts.filter(c => c.proposal === 'megatel').length}</p>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or company..."
              className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              data-testid="search-contacts-input"
            />
          </div>
        </div>

        {/* Contacts Table */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading contacts...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <Database className="text-slate-300 mx-auto mb-3" size={40} />
            <p className="text-slate-500 font-medium">No contacts yet</p>
            <p className="text-slate-400 text-sm mt-1">Contacts will appear here when someone requests proposal access.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="contacts-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-slate-600 font-semibold">Name</th>
                  <th className="text-left py-3 px-4 text-slate-600 font-semibold">Email</th>
                  <th className="text-left py-3 px-4 text-slate-600 font-semibold">Company</th>
                  <th className="text-left py-3 px-4 text-slate-600 font-semibold">Proposal</th>
                  <th className="text-left py-3 px-4 text-slate-600 font-semibold">Date</th>
                  <th className="text-right py-3 px-4 text-slate-600 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((contact) => (
                  <tr key={contact.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`contact-row-${contact.id}`}>
                    <td className="py-3 px-4 font-medium text-slate-800">{contact.name}</td>
                    <td className="py-3 px-4 text-blue-600">
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{contact.company || '—'}</td>
                    <td className="py-3 px-4">
                      <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium">{contact.proposal}</span>
                    </td>
                    <td className="py-3 px-4 text-slate-500">{new Date(contact.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                    <td className="py-3 px-4 text-right">
                      <button onClick={() => deleteContact(contact.id)} className="text-red-400 hover:text-red-600 transition-colors" data-testid={`delete-contact-${contact.id}`}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </div>
        )}
      </div>
    </div>
  );
};


export default AdminDashboard;
